import { useEffect, useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';

import { useBankFeedTransactionsHeaders } from 'domain/bankFeed/queries';
import { normalizeAgGridColDefs } from 'components/AgGrid/helpers';
import { notifyUserOfError } from 'lib/toasts';
import type { BankFeedAccountType } from 'domain/bankFeed/schemas';

type UseFetchColumnDefsArgs = {
  accountType: BankFeedAccountType;
};

export type UseFetchColumnDefsResult = {
  rawColumnDefs: ColDef[];
  rawColumnDefsIsFetched: boolean;
};

const useFetchColumnDefs = ({ accountType }: UseFetchColumnDefsArgs): UseFetchColumnDefsResult => {
  const { data, isFetched, isError, error } = useBankFeedTransactionsHeaders(accountType);

  // Surface toast on final fetch error (after retry exhaustion). Effect-based
  // instead of useQuery `onError` per services/query/CLAUDE.md — v5 drops the
  // callback and we avoid the divergence today.
  useEffect(() => {
    if (isError) notifyUserOfError(error);
  }, [isError, error]);

  const rawColumnDefs = useMemo<ColDef[]>(
    () => normalizeAgGridColDefs(data?.headers ?? []),
    [data],
  );

  return {
    rawColumnDefs,
    rawColumnDefsIsFetched: isFetched,
  };
};

export default useFetchColumnDefs;
